import type { FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { useEffect, useState } from 'react'
import { Reveal } from '@/components/motion/reveal'
import { ToneSection } from '@/components/generals/tone'
import { offices, siteConfig } from '@/constants/site'

const timeIn = (date: Date, timeZone: string): string => new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone
}).format(date)

const dayPart = (date: Date, timeZone: string): string => {
    const hour = Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hour12: false, timeZone }).format(date))

    if (hour >= 6 && hour < 12) return 'Morning'
    if (hour >= 12 && hour < 18) return 'Afternoon'
    if (hour >= 18 && hour < 23) return 'Evening'

    return 'Night shift'
}

interface OfficesProps {
    tone?: Tone
}

// Three cities, three clocks. Times render after mount to keep server and
// client markup identical.
export const Offices: FC<OfficesProps> = ({ tone = 'lilac' }): ReactNode => {
    const [now, setNow] = useState<Date | null>(null)

    useEffect(() => {
        setNow(new Date())

        const timer = window.setInterval(() => setNow(new Date()), 15000)

        return () => {
            window.clearInterval(timer)
        }
    }, [])

    return (
        <ToneSection tone={tone} id='offices' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
            <Reveal className='mb-14 flex flex-wrap items-end justify-between gap-6'>
                <h2 className='font-heading t-h2 font-semibold uppercase wdth-75'>
                    Around the clock,
                    <span className='block font-serif font-normal normal-case italic tracking-normal wdth-100'>
                        around the world.
                    </span>
                </h2>
                <a href={`mailto:${siteConfig.email}`} className='link-line text-lg font-semibold'>
                    {siteConfig.email}
                </a>
            </Reveal>

            <ul className='border-b border-tone-line'>
                {offices.map(office => (
                    <li key={office.city} className='grid items-end gap-4 border-t border-tone-line py-6 md:grid-cols-12 md:py-8'>
                        <span className='font-heading text-[clamp(3rem,9vw,9rem)] font-semibold uppercase leading-[0.82] tracking-[-0.045em] wdth-75 md:col-span-8'>
                            {office.city}
                        </span>
                        <span className='flex items-baseline justify-between gap-6 md:col-span-4 md:justify-end'>
                            <span className='t-label text-tone-muted'>
                                {now ? dayPart(now, office.timeZone) : 'Local time'}
                            </span>
                            <span className='font-mono text-3xl tabular-nums md:text-4xl'>
                                {now ? timeIn(now, office.timeZone) : '--:--'}
                            </span>
                        </span>
                    </li>
                ))}
            </ul>
        </ToneSection>
    )
}
